import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, Film } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InstantSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ movies: [] });
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (query.length < 2) {
      setResults({ movies: [] });
      setShowDropdown(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
        setResults(response.data);
        setShowDropdown(true);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setShowDropdown(false);
      setQuery('');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (id) => {
    setShowDropdown(false);
    setQuery('');
    navigate(`/film/${id}`);
  };

  const handleClear = () => {
    setQuery('');
    setResults({ movies: [] });
    setShowDropdown(false);
  };

  const totalResults = results.movies.length;

  return (
    <div className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Rechercher un film..."
          className="w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-full pl-10 pr-10 py-2 text-white placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-netflix-red focus:border-transparent transition-all"
        />
        {query && (
          <button onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        )}
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-netflix-red animate-spin" />
        )}
      </div>

      {showDropdown && totalResults > 0 && (
        <div ref={dropdownRef} className="absolute top-full mt-2 w-full bg-bg-secondary/95 backdrop-blur-xl border border-white/20 rounded-lg shadow-2xl max-h-96 overflow-y-auto z-50">
          <div className="p-2">
            <p className="text-xs text-text-secondary font-work uppercase px-3 py-2">
              Films ({results.movies.length})
            </p>
            {results.movies.slice(0, 8).map((movie) => (
              <button
                key={movie.id}
                onClick={() => handleResultClick(movie.id)}
                className="w-full flex items-center gap-3 p-3 hover:bg-white/10 rounded-lg transition-colors text-left"
              >
                {movie.poster_url ? (
                  <img src={movie.poster_url} alt={movie.title} className="w-12 h-16 object-cover rounded" />
                ) : (
                  <div className="w-12 h-16 bg-white/5 rounded flex items-center justify-center">
                    <Film className="w-6 h-6 text-text-secondary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-work text-sm truncate">{movie.title}</p>
                  <div className="flex items-center gap-2 text-xs text-text-secondary mt-1">
                    {movie.release_date && <span>{new Date(movie.release_date).getFullYear()}</span>}
                    {movie.vote_average && <span>&#11088; {movie.vote_average.toFixed(1)}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {totalResults > 8 && (
            <div className="p-2 border-t border-white/10">
              <button
                onClick={() => { navigate(`/search?q=${encodeURIComponent(query)}`); setShowDropdown(false); setQuery(''); }}
                className="w-full p-3 text-netflix-red hover:bg-white/10 rounded-lg transition-colors text-center font-work text-sm"
              >
                Voir tous les résultats ({totalResults})
              </button>
            </div>
          )}
        </div>
      )}

      {showDropdown && query.length >= 2 && totalResults === 0 && !loading && (
        <div ref={dropdownRef} className="absolute top-full mt-2 w-full bg-bg-secondary/95 backdrop-blur-xl border border-white/20 rounded-lg shadow-2xl p-4 z-50">
          <p className="text-text-secondary text-center font-inter text-sm">
            Aucun résultat pour &quot;{query}&quot;
          </p>
        </div>
      )}
    </div>
  );
};
